import { LinguiJSMessageDescriptor } from '@lib/types/common'
import { defineMessage, Trans } from '@lingui/macro'
import Link from 'next/link'
import { Ref } from '@lib/data/benefits-of-coding'

export type FaqContentType = {
  questionName: LinguiJSMessageDescriptor | string
  acceptedAnswerText?: LinguiJSMessageDescriptor | JSX.Element
  acceptedAnswerTextString: LinguiJSMessageDescriptor | string
}
export const FAQ_CONTENTS: FaqContentType[] = [
  {
    questionName: defineMessage({
      message: `Why should kids and teens learn to code?`,
    }),
    acceptedAnswerText: (
      <>
        <Link href="/coding-for-kids" prefetch={false}>
          <a className="text-orange mr-1 hover:underline">
            <Trans id="homepage.faq.answer1.linkText">
              Coding for kids and teens{' '}
            </Trans>
          </a>
        </Link>
        <Trans id="homepage.faq.answer1">
          helps improve their logical thinking, critical thinking,
          problem-solving skills, creativity, mathematics and writing skills and
          also gives them valuable skills in life and at the workplace. Coding
          provides a competitive advantage when applying to colleges,
          internships, and jobs. In addition, basic programming knowledge can
          change how kids and teens interact daily with the technologies they
          use. It’s a basic literacy–one we can’t afford to overlook.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Coding for kids and teens helps improve their logical thinking, critical thinking, problem-solving skills, creativity, mathematics and writing skills and also gives them valuable skills in life and at the workplace. Coding provides a competitive advantage when applying to colleges, internships, and jobs. In addition, basic programming knowledge can change how kids and teens interact daily with the technologies they use. It’s a basic literacy–one we can’t afford to overlook.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `My child is a beginner, is previous coding experience required?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          Our AI & coding classes for kids & teens have been developed so that a
          complete beginner can follow along without any trouble. With
          technology increasingly prevalent across so many industries, even
          basic coding knowledge can help kids and teens regardless of the
          career they ultimately decide to pursue.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Our AI & coding classes for kids & teens have been developed so that a complete beginner can follow along without any trouble. With technology increasingly prevalent across so many industries, even basic coding knowledge can help kids and teens regardless of the career they ultimately decide to pursue.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What is the recommended age for my child to start coding?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          Kids can start learning to code as early as 5-6 years old. When kids
          learn to code, it’s typically done under the guidance of a game. This
          makes the process fun and engaging while the kids are still learning a
          new skill. Our highly qualified Computer Science teachers at Codingal
          break the complex concepts into small digestible chunks for your
          child. Kids who learn to code when they’re young can set themselves up
          for a lifetime of opportunities for success. Our AI & coding classes
          for kids & teens start from age 5.
        </Trans>
      </>
    ),

    acceptedAnswerTextString: defineMessage({
      message: `Kids can start learning to code as early as 5-6 years old. When kids learn to code, it’s typically done under the guidance of a game. This makes the process fun and engaging while the kids are still learning a new skill. Our highly qualified Computer Science teachers at Codingal break the complex concepts into small digestible chunks for your child. Kids who learn to code when they’re young can set themselves up for a lifetime of opportunities for success. Our AI & coding classes for kids & teens start from age 5.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Do you offer online coding classes for kids?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          Yes. Codingal offers online coding classes for kids with live
          expert-led sessions, age-appropriate projects, and a structured K-12
          curriculum. You can also book a free trial class to get started.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes. Codingal offers online coding classes for kids with live expert-led sessions, age-appropriate projects, and a structured K-12 curriculum. You can also book a free trial class to get started.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What courses does Codingal offer?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          Codingal currently offers live online coding classes for kids and
          teens. Our coding courses include Scratch Programming, Python for kids
          and teens, AI and Machine Learning, App Development, Web Development,
          Game Development, Data Science for kids, and many other courses for
          Grade 1-12 students from age 5 years to age 17 years.
        </Trans>
      </>
    ),

    acceptedAnswerTextString: defineMessage({
      message: `Codingal currently offers live online coding classes for kids and teens. Our coding courses include Scratch Programming, Python for kids and teens, AI and Machine Learning, App Development, Web Development, Game Development, Data Science for kids, and many other courses for Grade 1-12 students from age 5 years to age 17 years.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `How will I stay updated on my child’s progress?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          A key aspect of our coding courses for kids and teens is keeping
          parents informed on what happens during the online coding class. We
          conduct the first Parent Teacher Meeting(PTM) after the 6th session,
          and the second meeting is conducted after the 12th session. As the
          course progresses, the PTM happens after every 12th session. Parents
          receive progress reports after every Parent Teacher Meeting that
          indicate what students have done during their classes and have links
          to their projects. Besides regular email updates on the kid&apos;s
          progress, parents and teachers are welcome to audit a current class or
          review recorded sessions of completed classes.
        </Trans>
      </>
    ),

    acceptedAnswerTextString: defineMessage({
      message: `A key aspect of our coding courses for kids and teens is keeping parents informed on what happens during the online coding class. We conduct the first Parent Teacher Meeting(PTM) after the 6th session, and the second meeting is conducted after the 12th session. As the course progresses, the PTM happens after every 12th session. Parents receive progress reports after every Parent Teacher Meeting that indicate what students have done during their classes and have links to their projects. Besides regular email updates on the kid's progress, parents and teachers are welcome to audit a current class or review recorded sessions of completed classes.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `How does Codingal select the best Coding Teachers?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          Codingal’s teachers are experts in computer science, backed with years
          of industry and teaching experience, and passionate about teaching
          kids and teens. We hire only the top 1% of applicants who are vetted
          for their technical skills and communication skills. A thorough
          background check is also done. We train our teachers to help them
          continue improving their skills with continuous teacher observation
          and feedback. Our teachers are best-in-class who are on a mission to
          inspire kids to fall in love with coding.
        </Trans>
      </>
    ),

    acceptedAnswerTextString: defineMessage({
      message: `Codingal’s teachers are experts in computer science, backed with years of industry and teaching experience, and passionate about teaching kids and teens. We hire only the top 1% of applicants who are vetted for their technical skills and communication skills. A thorough background check is also done. We train our teachers to help them continue improving their skills with continuous teacher observation and feedback. Our teachers are best-in-class who are on a mission to inspire kids to fall in love with coding.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `How do I teach coding to my child?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          Coding is a skill that requires specialized knowledge. While there are
          many resources available online that can help you catch up on the
          basics and start teaching coding to your child. However, the best and
          most effective way to teach a child to code is by ensuring they have a
          qualified and trained computer science teacher to guide them on their
          coding journey. Try a free 1-on-1{' '}
          <a
            href="https://www.codingal.com/"
            target="blank"
            rel="noreferrer"
            className="text-blue-200 underline"
          >
            coding class for kids and teens
          </a>{' '}
          to get started.
        </Trans>
      </>
    ),

    acceptedAnswerTextString: defineMessage({
      message: `Coding is a skill that requires specialized knowledge. While there are many resources available online that can help you catch up on the basics and start teaching coding to your child. However,  the best and most effective way to teach a child to code is by ensuring they have a qualified and trained computer science teacher to guide them on their coding journey. Try a free coding class for kids and teens to get started.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Can my child learn coding for free?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          There is no shortage of websites and platforms that offer free access
          to resources to teach coding to kids and teens. However, every child
          learns differently and at a different pace, and guided learning is a
          far more effective method for teaching kids to code. Coding is a
          specialized subject and Coding instructors with Computer Science
          background are best suited to teach it in a live format. Your child
          will develop interest in coding with right guidance from the teachers.
          You can try a free lesson with Codingal to experience the magical
          world of coding.
        </Trans>
      </>
    ),

    acceptedAnswerTextString: defineMessage({
      message: `There is no shortage of websites and platforms that offer free access to resources to teach coding to kids and teens. However, every child learns differently and at a different pace, and guided learning is a far more effective method for teaching kids to code. Coding is a specialized subject and Coding instructors with Computer Science background are best suited to teach it in a live format. Your child will develop interest in coding with right guidance from the teachers. You can try a free lesson with Codingal to experience the magical world of coding.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Are there any games to teach coding to kids?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          There are quite a few games that kids can learn coding from, like
          Minecraft, Code Monster, RoboZZle, Blockly Games, etc. are some of the
          popular ones.
        </Trans>
      </>
    ),

    acceptedAnswerTextString: defineMessage({
      message: `There are quite a few games that kids can learn coding from, like Minecraft, Code Monster, RoboZZle, Blockly Games, etc. are some of the popular ones.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What are the best apps for kids to learn coding?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          Many apps make coding fun and interactive for young children.
          Codingal, Scratch, Blockly, MIT App Inventor are suitable for those
          just starting. Intermediate coders who want to build an app or website
          can check out Codingal, CodeCombat, Stencyl, and Code.org. Advanced
          coders can also participate in coding competitions and hackathons
          organized by IOI, Codeforces, and Codingal.
        </Trans>
      </>
    ),

    acceptedAnswerTextString: defineMessage({
      message: `Many apps make coding fun and interactive for young children. Codingal, Scratch, Blockly, MIT App Inventor are suitable for those just starting. Intermediate coders who want to build an app or website can check out Codingal, CodeCombat, Stencyl, and Code.org. Advanced coders can also participate in coding competitions and hackathons organized by IOI, Codeforces, and Codingal.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Is the coding course schedule flexible?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          Yes! You can work the classes around your child’s schedule by
          selecting any time and any day that works for you.
        </Trans>
      </>
    ),

    acceptedAnswerTextString: defineMessage({
      message: `Yes! You can work the classes around your child’s schedule by selecting any time and any day that works for you.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What kind of device does my child need to start learning to code?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          All that is needed to learn coding with Codingal is a laptop/computer
          with a webcam and a stable internet connection. Classes can also be
          attended on a tablet/iPad with an external keyboard and working mic
          and camera.
        </Trans>
      </>
    ),

    acceptedAnswerTextString: defineMessage({
      message: `All that is needed to learn coding with Codingal is a laptop/computer with a webcam and a stable internet connection. Classes can also be attended on a tablet/iPad with an external keyboard and working mic and camera. `,
    }),
  },
  {
    questionName: defineMessage({
      message: `How can I trust Codingal?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          Codingal has been founded by leading edtech entrepreneurs, IIT
          graduates, Google & Amazon alumni and also backed by leading investors
          like Y Combinator and Rebright Partners. You can know more about us
          here - https://www.codingal.com/about-us/. Further, we provide money
          back guarantee if you decide to discontinue after enrollment due to
          any reason.
        </Trans>
      </>
    ),

    acceptedAnswerTextString: defineMessage({
      message: `Codingal has been founded by leading edtech entrepreneurs, IIT graduates, Google & Amazon alumni and also backed by leading investors like Y Combinator and Rebright Partners. You can know more about us here - https://www.codingal.com/about-us/. Further, we provide money back guarantee if you decide to discontinue after enrollment due to any reason.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What is the origin of the name “Codingal” ?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          Codingal name had the origin from “coding” + “magical”. Learning
          coding is a magical experience for the kids and hence, the name became
          “Codingal”.
        </Trans>
      </>
    ),

    acceptedAnswerTextString: defineMessage({
      message: `Codingal name had the origin from “coding” + “magical”. Learning coding is a magical experience for the kids and hence, the name became “Codingal”.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Why does Codingal have Penguin as logo and mascot?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          Penguins don&apos;t just look smart, they are highly intelligent, says
          study. Penguins are known for looking sharp. They have long been
          adored for their waddling gait and striking black and white attire
          that gives them the appearance of a flock of dinner jackets. Because
          Coding gives superpower, it makes everyone learning coding smarter and
          more intelligent in today&apos;s world of technology. We chose
          “Penguin” as our logo. We also call our penguin dearly with the name
          Cody!
        </Trans>
      </>
    ),

    acceptedAnswerTextString: defineMessage({
      message: `Penguins don't just look smart, they are highly intelligent, says study. Penguins are known for looking sharp. They have long been adored for their waddling gait and striking black and white attire that gives them the appearance of a flock of dinner jackets. Because Coding gives superpower, it makes everyone learning coding smarter and more intelligent in today's world of technology. We chose “Penguin” as our logo. We also call our penguin dearly with the name Cody!`,
    }),
  },
]

export const QUIZZES_FAQ_CONTENTS: FaqContentType[] = [
  {
    questionName: defineMessage({
      message: `Why should kids learn to code?`,
    }),
    acceptedAnswerText: (
      <>
        <Link href="/coding-for-kids" prefetch={false}>
          <a className="text-orange mr-1 hover:underline">
            <Trans id="quizzes.faq.answer1.linkText">
              Coding for kids and teens{' '}
            </Trans>
          </a>
        </Link>
        <Trans id="quizzes.faq.answer1">
          helps creativity, communication, self-discipline, and many other
          necessary traits. Coding quizzes are individually motivated
          activities, making it a fun and effective way to teach children early
          on how to probe their knowledge about Computer Science and various
          programming languages.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Coding for kids and teens helps creativity, communication, self-discipline, and many other necessary traits. Coding quizzes are individually motivated activities, making it a fun and effective way to teach children early on how to probe their knowledge about Computer Science and various programming languages.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What are the benefits of coding quizzes for kids and teens?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans id="quizzes.faq.answer2">
          Coding quizzes for kids and teens can be an exceptionally effective
          way to learn. Taking quizzes can produce better recall of facts,
          motivate kids, and help them assess their knowledge.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Coding quizzes for kids and teens can be an exceptionally effective way to learn. Taking quizzes can produce better recall of facts, motivate kids, and help them assess their knowledge.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Are coding quizzes effective in learning?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans id="quizzes.faq.answer3">
          Coding quizzes are fun activities for kids and teens. They are also a
          sneaky form of learning as they don&apos;t feel like a traditional
          activity. Quizzes can help kids practice existing Computer Science
          knowledge while stimulating interest in learning about new coding
          topics.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Coding quizzes are fun activities for kids and teens. They are also a sneaky form of learning as they don't feel like a traditional activity. Quizzes can help kids practice existing Computer Science knowledge while stimulating interest in learning about new coding topics.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Are coding quizzes good for my child’s brain development?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans id="quizzes.faq.answer4">
          Yes, coding quizzes are good for a child&apos;s brain development. An
          interactive coding quiz is fun, helps knowledge retention, and expands
          your kid&apos;s brain capacity.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes, coding quizzes are good for a child's brain development. An interactive coding quiz is fun, helps knowledge retention, and expands your kid's brain capacity.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Is there any negative marking for wrong answers?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans id="quizzes.faq.answer5">
          No, there is no negative marking for wrong answers. In case of late
          submission, if the solution submitted is wrong, no points will be
          given and half points will be given in case of the correct answer.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `No, there is no negative marking for wrong answers. In case of late submission, if the solution submitted is wrong, no points will be given and half points will be given in case of the correct answer.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Is there a time limit while attempting the quiz?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans id="quizzes.faq.answer6">
          Every problem in the quiz is given a certain time limit depending on
          the question asked.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Every problem in the quiz is given a certain time limit depending on the question asked.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `How many times can I attempt a quiz?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans id="quizzes.faq.answer7">
          You can attempt a quiz any number of times. There is no limit on
          attempting quizzes. After a quiz is completed, a play again option
          will be available on this page.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `You can attempt a quiz any number of times. There is no limit on attempting quizzes. After a quiz is completed, a play again option will be available on this page.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `How do I teach coding to my child?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans id="quizzes.faq.answer8">
          Coding is a skill that requires specialized knowledge. While there are
          many resources available online that can help you catch up on the
          basics and start teaching coding to your child. However, the best and
          most effective way to teach a child to code is by ensuring they have a
          qualified and trained computer science teacher to guide them on their
          coding journey. Try a free 1-on-1 coding class for kids to get
          started.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Coding is a skill that requires specialized knowledge. While there are many resources available online that can help you catch up on the basics and start teaching coding to your child. However, the best and most effective way to teach a child to code is by ensuring they have a qualified and trained computer science teacher to guide them on their coding journey. Try a free coding class for kids to get started.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Are there any games to teach coding to kids?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans id="quizzes.faq.answer9">
          There are quite a few games that kids can learn coding from, like
          Minecraft, Code Monster, RoboZZle, Blockly Games, etc. are some of the
          popular ones.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `There are quite a few games that kids can learn coding from, like Minecraft, Code Monster, RoboZZle, Blockly Games, etc. are some of the popular ones.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What are the best apps for kids to learn coding?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans id="quizzes.faq.answer10">
          Many apps make coding fun and interactive for young children.
          Codingal, Scratch, Blockly, MIT App Inventor are suitable for those
          just starting. Intermediate coders who want to build an app or website
          can check out Codingal, CodeCombat, Stencyl, and Code.org. Advanced
          coders can also participate in coding competitions and hackathons
          organized by IOI, Codeforces, and Codingal.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Many apps make coding fun and interactive for young children. Codingal, Scratch, Blockly, MIT App Inventor are suitable for those just starting. Intermediate coders who want to build an app or website can check out Codingal, CodeCombat, Stencyl, and Code.org. Advanced coders can also participate in coding competitions and hackathons organized by IOI, Codeforces, and Codingal.`,
    }),
  },
]

export const CODINGAL_VS_WHITEHATJS_FAQ: FaqContentType[] = [
  {
    questionName: defineMessage({
      message: `What is the biggest difference between Codingal’s online coding classes and Whitehat Jr. classes?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans id="codingalVsWhitehatJr.faq.answer1">
          All our coding classes are conducted by expert Computer Science
          teachers, unlike Whitehat Jr, where teachers are from varied
          backgrounds. Our curriculum is based on BIDE, STEAM, and Bloom’s
          taxonomy to develop 8 core competencies in your child. In addition,
          the students get a STEM.org accredited certificate on successfully
          completing a free trial class.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `All our coding classes are conducted by expert Computer Science teachers, unlike Whitehat Jr, where teachers are from varied backgrounds. Our curriculum is based on BIDE, STEAM, and Bloom’s taxonomy to develop 8 core competencies in your child. In addition, the students get a STEM.org accredited certificate on successfully completing a free trial class.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Are coding classes by Codingal better?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans id="codingalVsWhitehatJr.faq.answer2">
          Codingal’s mission is to help your child fall in love with coding. We
          believe that success will surely follow if a child enjoys coding and
          falls in love with it. We don’t believe in creating a stressful
          experience for you and your child by scaring you into buying our
          product. And most importantly, we won’t hound you with spam calls and
          harass you into buying our product.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Codingal’s mission is to help your child fall in love with coding. We believe that success will surely follow if a child enjoys coding and falls in love with it. We don’t believe in creating a stressful experience for you and your child by scaring you into buying our product. And most importantly, we won’t hound you with spam calls and harass you into buying our product.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Will my child get access to free resources if I enrol my child in Codingal?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans id="codingalVsWhitehatJr.faq.answer3">
          Yes. Your child will get lifetime access to all the learning
          resources, coding competitions, quizzes, and more.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes. Your child will get lifetime access to all the learning resources, coding competitions, quizzes, and more.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Will my child learn coding only from experts at Codingal?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans id="codingalVsWhitehatJr.faq.answer4">
          Yes. Codingal’s teachers are experts in computer science, backed with
          years of industry and teaching experience, and passionate about
          teaching kids. We hire only the top 1% of applicants who are vetted
          for their technical skills and communication skills. A thorough
          background check is also done. We train our teachers to help them
          continue improving their skills with continuous teacher observation
          and feedback. Our teachers are best-in-class who are on a mission to
          inspire kids to fall in love with coding.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes. Codingal’s teachers are experts in computer science, backed with years of industry and teaching experience, and passionate about teaching kids. We hire only the top 1% of applicants who are vetted for their technical skills and communication skills. A thorough background check is also done. We train our teachers to help them continue improving their skills with continuous teacher observation and feedback. Our teachers are best-in-class who are on a mission to inspire kids to fall in love with coding.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Why does Codingal offer more value for the price than Whitehat Jr?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans id="codingalVsWhitehatJr.faq.answer5">
          Our{' '}
          <a
            href="/coding-for-kids/"
            className="text-orange mr-1 hover:underline"
          >
            Coding for kids
          </a>{' '}
          courses are holistic and our curriculum is scientifically based. Our
          prices are transparent and predictable. Further, we provide money back
          guarantee if you decide to discontinue after enrollment due to any
          reason.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Our coding for kids courses are holistic and our curriculum is scientifically based. Our prices are transparent and predictable. Further, we provide money back guarantee if you decide to discontinue after enrollment due to any reason.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `How can I trust Codingal?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans id="codingalVsWhitehatJr.faq.answer6">
          Codingal has been founded by leading edtech entrepreneurs, IIT
          graduates, Google & Amazon alumni and also backed by leading investors
          like Y Combinator and Rebright Partners. You can know more about us
          here
        </Trans>{' '}
        -{' '}
        <a href="/about-us/" className="text-orange mr-1 hover:underline">
          https://www.codingal.com/about-us/
        </a>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Codingal has been founded by leading edtech entrepreneurs, IIT graduates, Google & Amazon alumni and also backed by leading investors like Y Combinator and Rebright Partners. You can know more about us here - https://www.codingal.com/about-us/`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What courses does Codingal offer?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans id="codingalVsWhitehatJr.faq.answer7">
          Codingal currently offers live online coding classes for kids. Our
          coding courses include{' '}
          <a
            href="/courses/scratch-programming/"
            className="text-orange mr-1 hover:underline"
          >
            Scratch Programming
          </a>
          ,{' '}
          <a
            href="/courses/python-for-kids/"
            className="text-orange mr-1 hover:underline"
          >
            Python for kids
          </a>
          ,{' '}
          <a
            href="https://www.codingal.com/courses/app-development/"
            className="text-orange mr-1 hover:underline"
          >
            App Development
          </a>
          ,{' '}
          <a
            href="https://www.codingal.com/courses/web-development/"
            className="text-orange mr-1 hover:underline"
          >
            Web Development
          </a>
          ,{' '}
          <a
            href="https://www.codingal.com/courses/game-development/"
            className="text-orange mr-1 hover:underline"
          >
            Game Development
          </a>
          ,{' '}
          <a
            href="https://www.codingal.com/courses/data-science/"
            className="text-orange mr-1 hover:underline"
          >
            Data Science for kids
          </a>
          , and many other courses for Grade 1-12 students from age 5 years to
          age 17 years.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Codingal currently offers live online coding classes for kids. Our coding courses include Scratch Programming, Python for kids, App Development, Web Development, Game Development, Data Science for kids, and many other courses for Grade 1-12 students from age 5 years to age 17 years.`,
    }),
  },
]

// FAQs: Coding for Kids
// Note: questionName can be swapped to Lingui descriptors later if needed.

export const CODING_FOR_KIDS_FAQ: FaqContentType[] = [
  {
    questionName: 'What is coding for kids?',
    acceptedAnswerTextString:
      'Coding for kids means learning how to give a computer step by step instructions to create games, animations, apps, and websites. Kids usually start with block coding and later move to typed languages like Python.',
    acceptedAnswerText: (
      <>
        Coding for kids means learning how to give a computer step by step
        instructions to create games, animations, apps, and websites. Kids
        usually start with block coding and later move to typed languages like
        Python.
      </>
    ),
  },
  {
    questionName: 'What are the benefits of coding for kids?',
    acceptedAnswerTextString:
      'Coding helps kids build problem solving, logical thinking, creativity, and confidence. It also helps kids understand how technology works in everyday life.',
    acceptedAnswerText: (
      <>
        Coding helps kids build problem solving, logical thinking, creativity,
        and confidence. It also helps kids understand how technology works in
        everyday life.
      </>
    ),
  },
  {
    questionName: 'What is the best age to start coding for kids?',
    acceptedAnswerTextString:
      'Many kids can start around age 5 or 6 with beginner friendly block coding. Older kids can move faster into Python, app development, web development, and AI projects based on interest.',
    acceptedAnswerText: (
      <>
        Many kids can start around age 5 or 6 with beginner friendly block
        coding. Older kids can move faster into Python, app development, web
        development, and AI projects based on interest.
      </>
    ),
  },
  {
    questionName:
      'Does my child need experience to join a coding class for kids?',
    acceptedAnswerTextString:
      'No. Beginner classes start from the basics and use simple, guided steps. A free trial lesson can help confirm the right level.',
    acceptedAnswerText: (
      <>
        No. Beginner classes start from the basics and use simple, guided steps.
        A free trial lesson can help confirm the right level.
      </>
    ),
  },
  {
    questionName:
      'Do you offer AI & coding classes for kids in 1:1 and groups?',
    acceptedAnswerTextString:
      "Yes. Codingal offers live online coding classes for kids, including AI & coding classes in both 1:1 and small group formats. Parents can choose the learning option that best fits their child's goals, learning style, and schedule",
    acceptedAnswerText: (
      <>
        Yes. Codingal offers live online{' '}
        <a
          className="text-orange mr-1 hover:underline"
          href="https://www.codingal.com/"
        >
          coding classes for kids
        </a>{' '}
        , including AI & coding classes in both 1:1 and small group formats.
        Parents can choose the learning option that best fits their child&apos;s
        goals, learning style, and schedule
      </>
    ),
  },
  {
    questionName: 'What happens in a free AI & coding trial lesson?',
    acceptedAnswerTextString:
      'Your child meets an instructor live, builds a small project, and learns the basics through guided steps. After the class, you get a level recommendation and course options.',
    acceptedAnswerText: (
      <>
        Your child meets an instructor live, builds a small project, and learns
        the basics through guided steps. After the class, you get a level
        recommendation and course options.
      </>
    ),
  },
  {
    questionName: 'What are coding games for kids?',
    acceptedAnswerTextString:
      'Coding games are projects kids build using concepts like loops and conditionals. Examples include maze games, quiz games, runner games, and interactive stories.',
    acceptedAnswerText: (
      <>
        Coding games are projects kids build using concepts like loops and
        conditionals. Examples include maze games, quiz games, runner games, and
        interactive stories.
      </>
    ),
  },
  {
    questionName: 'What is Scratch coding for kids?',
    acceptedAnswerTextString:
      'Scratch is a block based coding platform where kids drag and drop blocks to build games and animations. It is one of the easiest ways for beginners to start coding. Explore Scratch coding for kids https://www.codingal.com/courses/scratch-programming/',
    acceptedAnswerText: (
      <>
        Scratch is a block based coding platform where kids drag and drop blocks
        to build games and animations. It is one of the easiest ways for
        beginners to start coding.
        <br />
        <a
          className="text-orange mr-1 hover:underline"
          href="https://www.codingal.com/courses/scratch-programming/"
        >
          Explore Scratch coding for kids
        </a>
      </>
    ),
  },
  {
    questionName: 'Is Minecraft coding good for kids?',
    acceptedAnswerTextString:
      'Minecraft coding is great for kids who love building. It turns coding into fun missions where kids automate builds and explore logic through play.',
    acceptedAnswerText: (
      <>
        Minecraft coding is great for kids who love building. It turns coding
        into fun missions where kids automate builds and explore logic through
        play.
      </>
    ),
  },
  {
    questionName: 'Is Python coding good for kids?',
    acceptedAnswerTextString:
      'Python is great for kids who are ready to type code. It is beginner friendly and powerful enough for games, apps, and beginner AI projects. Explore Python coding for kids https://www.codingal.com/courses/python-for-kids/',
    acceptedAnswerText: (
      <>
        Python is great for kids who are ready to type code. It is beginner
        friendly and powerful enough for games, apps, and beginner AI projects.
        <br />
        <a
          className="text-orange mr-1 hover:underline"
          href="https://www.codingal.com/courses/python-for-kids/"
        >
          Explore Python coding for kids
        </a>
      </>
    ),
  },
  {
    questionName: 'What is a coding program for kids?',
    acceptedAnswerTextString:
      'A coding program for kids is a structured learning path that builds skills step by step through projects and practice. It helps kids progress from beginner to advanced levels.',
    acceptedAnswerText: (
      <>
        A coding program for kids is a structured learning path that builds
        skills step by step through projects and practice. It helps kids
        progress from beginner to advanced levels.
      </>
    ),
  },
  {
    questionName: 'What is a coding school for kids?',
    acceptedAnswerTextString:
      'A coding school for kids is a learning provider that offers structured instruction, projects, and guidance. Online coding schools offer the same expert teaching with more flexibility for families.',
    acceptedAnswerText: (
      <>
        A coding school for kids is a learning provider that offers structured
        instruction, projects, and guidance. Online coding schools offer the
        same expert teaching with more flexibility for families.
      </>
    ),
  },
  {
    questionName: 'What are the best coding websites for kids?',
    acceptedAnswerTextString:
      'The best coding websites are beginner friendly, project based, and level structured. Look for platforms that offer clear progress, real projects, and support when kids get stuck.',
    acceptedAnswerText: (
      <>
        The best coding websites are beginner friendly, project based, and level
        structured. Look for platforms that offer clear progress, real projects,
        and support when kids get stuck.
      </>
    ),
  },
  {
    questionName: 'What are the best coding apps for kids?',
    acceptedAnswerTextString:
      'Coding apps can be helpful for practice and short activities. For deeper learning, live classes and structured courses help kids build full projects with guidance.',
    acceptedAnswerText: (
      <>
        Coding apps can be helpful for practice and short activities. For deeper
        learning, live classes and structured courses help kids build full
        projects with guidance.
      </>
    ),
  },
  {
    questionName: 'What is a coding camp for kids?',
    acceptedAnswerTextString:
      'A coding camp for kids is a short, focused program where kids build multiple projects in a set schedule. It is ideal for fast progress and high engagement. You can explore a summer coding camp for kids or a winter coding camp for kids based on your child’s schedule. summer coding camp for kids https://www.codingal.com/kids-teens-coding-camps/summer/ winter coding camp for kids https://www.codingal.com/kids-teens-coding-camps/winter/',
    acceptedAnswerText: (
      <>
        A coding camp for kids is a short, focused program where kids build
        multiple projects in a set schedule. It is ideal for fast progress and
        high engagement. You can explore a summer coding camp for kids or a
        winter coding camp for kids based on your child’s schedule.
        <br />
        <ul>
          <li>
            <a
              className="text-orange mr-1 hover:underline"
              href="https://www.codingal.com/kids-teens-coding-camps/summer/"
            >
              summer coding camp for kids
            </a>
          </li>
          <li>
            <a
              className="text-orange mr-1 hover:underline"
              href="https://www.codingal.com/kids-teens-coding-camps/winter/"
            >
              winter coding camp for kids
            </a>
          </li>
        </ul>
        <br />
      </>
    ),
  },
  {
    questionName: 'Do you offer game development for kids?',
    acceptedAnswerTextString:
      'Yes. Kids who love games can learn coding by building game projects step by step. Explore game development for kids https://www.codingal.com/courses/game-development/',
    acceptedAnswerText: (
      <>
        Yes. Kids who love games can learn coding by building game projects step
        by step.
        <br />
        <a
          className="text-orange mr-1 hover:underline"
          href="https://www.codingal.com/courses/game-development/"
        >
          Explore game development for kids
        </a>
      </>
    ),
  },
  {
    questionName: 'Do you offer app development for kids?',
    acceptedAnswerTextString:
      'Yes. App development is a fun way for kids to learn how apps work and build their own simple projects with guided support. Explore app development for kids https://www.codingal.com/courses/app-development/',
    acceptedAnswerText: (
      <>
        Yes. App development is a fun way for kids to learn how apps work and
        build their own simple projects with guided support.
        <br />
        <a
          className="text-orange mr-1 hover:underline"
          href="https://www.codingal.com/courses/app-development/"
        >
          Explore app development for kids
        </a>
      </>
    ),
  },
  {
    questionName: 'Do you have coding quizzes for kids?',
    acceptedAnswerTextString:
      'Yes. Coding quizzes are a quick way to practice fundamentals like logic, loops, variables, and debugging. Explore coding quizzes for kids https://www.codingal.com/quizzes/',
    acceptedAnswerText: (
      <>
        Yes. Coding quizzes are a quick way to practice fundamentals like logic,
        loops, variables, and debugging.
        <br />
        <a
          className="text-orange mr-1 hover:underline"
          href="https://www.codingal.com/quizzes/"
        >
          Explore coding quizzes for kids
        </a>
      </>
    ),
  },
  {
    questionName: 'How do I choose the right AI & coding class for my child?',
    acceptedAnswerTextString:
      'Start with a free trial lesson. Based on your child’s age, interests, and comfort level, you can choose between Scratch, game development, app development, Python, and other pathways.',
    acceptedAnswerText: (
      <>
        Start with a free trial lesson. Based on your child’s age, interests,
        and comfort level, you can choose between Scratch, game development, app
        development, Python, and other pathways.
      </>
    ),
  },
]

export const REVIEWS_AND_TESTIMONIALS_FAQ: FaqContentType[] = [
  {
    questionName: defineMessage({
      message: `What is Codingal?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          Codingal is an online learning platform offering live, instructor-led
          classes across coding, AI, math, and communication for school-age
          students.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Codingal is an online learning platform offering live, instructor-led classes across coding, AI, math, and communication for school-age students.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Is Codingal an online coding school for kids?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          Yes. Codingal offers live coding classes with structured learning
          paths and project-based practice for school-age learners.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes. Codingal offers live coding classes with structured learning paths and project-based practice for school-age learners.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Is Codingal an after-school learning program?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          Yes. Codingal fits alongside school as an after-school learning option
          with live classes and guided practice.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes. Codingal fits alongside school as an after-school learning option with live classes and guided practice.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Are there after-school programs for kids on Codingal?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          Yes. Codingal offers after-school programs across coding, AI, math,
          and communication, based on age and learning goals.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes. Codingal offers after-school programs across coding, AI, math, and communication, based on age and learning goals.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Does Codingal offer teen coding programs?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          Yes. Codingal offers structured teen coding pathways with
          age-appropriate progression and real-world projects.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes. Codingal offers structured teen coding pathways with age-appropriate progression and real-world projects.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `How are Codingal classes conducted?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          Classes are live and interactive, led by trained instructors. Students
          actively participate and build projects during sessions.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Classes are live and interactive, led by trained instructors. Students actively participate and build projects during sessions.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `How is progress tracked?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          Progress is tracked through completed projects, activities, dashboard
          reports, and monthly parent-teacher meetings.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Progress is tracked through completed projects, activities, dashboard reports, and monthly parent-teacher meetings.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `How much does Codingal cost?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          Pricing depends on the selected program and learning plan. Full
          details are shared before enrolment.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Pricing depends on the selected program and learning plan. Full details are shared before enrolment.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Are scholarships available?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          Yes. Codingal offers merit-based and need-based scholarships for
          eligible students.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes. Codingal offers merit-based and need-based scholarships for eligible students.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Does Codingal offer a free trial?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          Yes. Families can attend a free live lesson to experience the
          classroom before deciding.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes. Families can attend a free live lesson to experience the classroom before deciding.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `How is Codingal rated by parents?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          Codingal is rated Excellent on Trustpilot and 4.6/5 on Google Reviews,
          based on independent parent feedback.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Codingal is rated Excellent on Trustpilot and 4.6/5 on Google Reviews, based on independent parent feedback.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Where do Codingal students come from?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          Students learn from over 2,000 cities across 130+ countries.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Students learn from over 2,000 cities across 130+ countries.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Who founded Codingal?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          Codingal was founded by alumni of leading global technology companies
          including Google and Amazon.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Codingal was founded by alumni of leading global technology companies including Google and Amazon.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Who supports and backs Codingal?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          Codingal is backed by top investors such as Y Combinator and Rebright
          Partners.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Codingal is backed by top investors such as Y Combinator and Rebright Partners.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Is Codingal’s curriculum accredited?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          Yes. Codingal’s programs are accredited by STEM.org for educational
          excellence.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes. Codingal’s programs are accredited by STEM.org for educational excellence.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What makes Codingal a trusted and reliable learning platform?`,
    }),
    acceptedAnswerText: (
      <>
        <Trans>
          Codingal combines experienced founders, global investor backing, an
          accredited curriculum, transparent parent reviews, and a structured
          live learning model to ensure a dependable and high-quality
          educational experience.
        </Trans>
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Codingal combines experienced founders, global investor backing, an accredited curriculum, transparent parent reviews, and a structured live learning model to ensure a dependable and high-quality educational experience.`,
    }),
  },
]

export const TEACH_FAQS: FaqContentType[] = [
  {
    questionName: 'How do I apply to become a teacher at Codingal?',
    acceptedAnswerText: (
      <>
        Fill out the application form, verify your mobile number, and complete
        the required assessment rounds. Shortlisted candidates move to
        interview, onboarding, and training.
      </>
    ),
    acceptedAnswerTextString:
      'Fill out the application form, verify your mobile number, and complete the required assessment rounds. Shortlisted candidates move to interview, onboarding, and training.',
  },
  {
    questionName: 'What are the requirements to teach at Codingal?',
    acceptedAnswerText: (
      <>
        You need strong subject knowledge, clear spoken English, and a laptop or
        desktop with stable internet. A webcam, microphone, and quiet teaching
        setup are also important.
      </>
    ),
    acceptedAnswerTextString:
      'You need strong subject knowledge, clear spoken English, and a laptop or desktop with stable internet. A webcam, microphone, and quiet teaching setup are also important.',
  },
  {
    questionName: 'Is teaching at Codingal remote?',
    acceptedAnswerText: (
      <>
        Yes, Codingal teaching roles are fully remote. You can apply, train, and
        teach online from home.
      </>
    ),
    acceptedAnswerTextString:
      'Yes, Codingal teaching roles are fully remote. You can apply, train, and teach online from home.',
  },
  {
    questionName: 'Which subjects can I teach at Codingal?',
    acceptedAnswerText: (
      <>
        You can apply for AI &amp; Coding, Math, or Public Speaking &amp;
        Communication. Your final track depends on your skills and selection
        results.
      </>
    ),
    acceptedAnswerTextString:
      'You can apply for AI & Coding, Math, or Public Speaking & Communication. Your final track depends on your skills and selection results.',
  },
  {
    questionName: 'Is there a test to become a Codingal teacher?',
    acceptedAnswerText: (
      <>
        Yes, candidates usually take a proctored subject test. It helps assess
        subject understanding before moving to the next rounds.
      </>
    ),
    acceptedAnswerTextString:
      'Yes, candidates usually take a proctored subject test. It helps assess subject understanding before moving to the next rounds.',
  },
  {
    questionName: 'How long does the selection process take?',
    acceptedAnswerText: (
      <>
        The process usually takes around 15 days. Timelines can vary based on
        test completion and interview availability.
      </>
    ),
    acceptedAnswerTextString:
      'The process usually takes around 15 days. Timelines can vary based on test completion and interview availability.',
  },
  {
    questionName: 'What happens after I click Apply?',
    acceptedAnswerText: (
      <>
        You verify your number, create your profile, fill out the form, and
        begin the evaluation process. Qualified candidates move ahead to the
        next rounds.
      </>
    ),
    acceptedAnswerTextString:
      'You verify your number, create your profile, fill out the form, and begin the evaluation process. Qualified candidates move ahead to the next rounds.',
  },
  {
    questionName: 'Do teachers get students automatically?',
    acceptedAnswerText: (
      <>
        Class allocation depends on availability, demand, performance, and
        student feedback. Consistent teachers usually get better scheduling over
        time.
      </>
    ),
    acceptedAnswerTextString:
      'Class allocation depends on availability, demand, performance, and student feedback. Consistent teachers usually get better scheduling over time.',
  },
  {
    questionName: 'How does teacher pay work at Codingal?',
    acceptedAnswerText: (
      <>
        Payouts are based on completed classes and the program structure. Some
        programs may also include incentives or minimum guarantees.
      </>
    ),
    acceptedAnswerTextString:
      'Payouts are based on completed classes and the program structure. Some programs may also include incentives or minimum guarantees.',
  },
  {
    questionName: 'How much do Codingal teachers earn?',
    acceptedAnswerText: (
      <>
        Codingal teachers earn ₹46,000 per month on average. Top-performing
        teachers earn up to ₹1,85,000 per month. In 2025, Codingal paid out ₹17
        crore to its teaching community. Earnings depend on the number of
        classes taught, consistency, and student feedback.
      </>
    ),
    acceptedAnswerTextString:
      'Codingal teachers earn ₹46,000 per month on average. Top-performing teachers earn up to ₹1,85,000 per month. In 2025, Codingal paid out ₹17 crore to its teaching community. Earnings depend on the number of classes taught, consistency, and student feedback.',
  },
  {
    questionName: 'Do I need to make lesson plans for every class?',
    acceptedAnswerText: (
      <>
        No, Codingal provides a structured curriculum and class flow. Teachers
        mainly focus on delivery, student support, and progress tracking.
      </>
    ),
    acceptedAnswerTextString:
      'No, Codingal provides a structured curriculum and class flow. Teachers mainly focus on delivery, student support, and progress tracking.',
  },
  {
    questionName: 'How are Codingal teaching sessions structured?',
    acceptedAnswerText: (
      <>
        Teaching usually happens in live 1:1 and small-group classes. Sessions
        are designed to be interactive and student-focused.
      </>
    ),
    acceptedAnswerTextString:
      'Teaching usually happens in live 1:1 and small-group classes. Sessions are designed to be interactive and student-focused.',
  },
  {
    questionName: 'Do I need to keep my camera on while teaching?',
    acceptedAnswerText: (
      <>
        Yes, teachers are expected to keep their camera on during interviews and
        live classes. This helps with engagement, trust, and classroom
        professionalism.
      </>
    ),
    acceptedAnswerTextString:
      'Yes, teachers are expected to keep their camera on during interviews and live classes. This helps with engagement, trust, and classroom professionalism.',
  },
  {
    questionName: 'What training do selected teachers receive?',
    acceptedAnswerText: (
      <>
        Selected teachers go through onboarding and training before starting
        live classes. This includes platform guidance, teaching expectations,
        and workflow support.
      </>
    ),
    acceptedAnswerTextString:
      'Selected teachers go through onboarding and training before starting live classes. This includes platform guidance, teaching expectations, and workflow support.',
  },
  {
    questionName: 'Can I apply through referral or LinkedIn?',
    acceptedAnswerText: (
      <>
        Yes, you can apply through referrals, LinkedIn, or job boards. All
        applicants go through the same selection process.
      </>
    ),
    acceptedAnswerTextString:
      'Yes, you can apply through referrals, LinkedIn, or job boards. All applicants go through the same selection process.',
  },
  {
    questionName: 'Is Codingal legit for teachers?',
    acceptedAnswerText: (
      <>
        Codingal is a trusted online learning platform with a structured hiring
        process, training support, and transparent systems for teaching and
        payouts.
      </>
    ),
    acceptedAnswerTextString:
      'Codingal is a trusted online learning platform with a structured hiring process, training support, and transparent systems for teaching and payouts.',
  },
  {
    questionName: 'Is Codingal a good platform for online teaching jobs?',
    acceptedAnswerText: (
      <>
        Codingal offers flexible remote teaching opportunities for educators who
        want to teach kids online. It is well-suited for teachers looking for
        structured online teaching work.
      </>
    ),
    acceptedAnswerTextString:
      'Codingal offers flexible remote teaching opportunities for educators who want to teach kids online. It is well-suited for teachers looking for structured online teaching work.',
  },
  {
    questionName: 'Are there online teaching jobs at Codingal?',
    acceptedAnswerText: (
      <>
        Yes. Codingal hires remote teachers for live online classes across AI
        &amp; Coding, Math, and Public Speaking &amp; Communication, with
        training, curriculum, and teacher support built into the system.
      </>
    ),
    acceptedAnswerTextString:
      'Yes. Codingal hires remote teachers for live online classes across AI & Coding, Math, and Public Speaking & Communication, with training, curriculum, and teacher support built into the system.',
  },
  {
    questionName: 'Can I teach online from home with Codingal?',
    acceptedAnswerText: (
      <>
        Yes. Teaching at Codingal is remote, and teachers need a laptop or
        desktop, stable internet, a webcam, microphone, and a classroom-ready
        setup.
      </>
    ),
    acceptedAnswerTextString:
      'Yes. Teaching at Codingal is remote, and teachers need a laptop or desktop, stable internet, a webcam, microphone, and a classroom-ready setup.',
  },
  {
    questionName: 'How do I become a coding tutor online at Codingal?',
    acceptedAnswerText: (
      <>
        To become a coding tutor online at Codingal, apply on the teach page,
        choose the AI &amp; Coding track, complete the application form, take
        the required test rounds, and attend the final interview. Selected
        candidates then move to onboarding and training.
      </>
    ),
    acceptedAnswerTextString:
      'To become a coding tutor online at Codingal, apply on the teach page, choose the AI & Coding track, complete the application form, take the required test rounds, and attend the final interview. Selected candidates then move to onboarding and training.',
  },
  {
    questionName: 'Can I teach coding to kids online at Codingal?',
    acceptedAnswerText: (
      <>
        Yes. Codingal&apos;s teach page is built around teaching kids in Grades
        1-12 through live 1:1 and small-group classes, including AI &amp; Coding
        tracks.
      </>
    ),
    acceptedAnswerTextString:
      "Yes. Codingal's teach page is built around teaching kids in Grades 1-12 through live 1:1 and small-group classes, including AI & Coding tracks.",
  },
  {
    questionName: 'How do I become a math tutor at Codingal?',
    acceptedAnswerText: (
      <>
        You can apply through the teach page, select the Math track, complete
        the application form, take the proctored subject test, clear the
        language-readiness round, and attend the final interview.
      </>
    ),
    acceptedAnswerTextString:
      'You can apply through the teach page, select the Math track, complete the application form, take the proctored subject test, clear the language-readiness round, and attend the final interview.',
  },
  {
    questionName: 'Can I become a math tutor online with Codingal?',
    acceptedAnswerText: (
      <>
        Yes. Codingal offers remote Math teaching opportunities for educators
        who want to teach students online with a structured curriculum,
        training, and teacher support.
      </>
    ),
    acceptedAnswerTextString:
      'Yes. Codingal offers remote Math teaching opportunities for educators who want to teach students online with a structured curriculum, training, and teacher support.',
  },
  {
    questionName: 'Can I teach kids English online at Codingal?',
    acceptedAnswerText: (
      <>
        Codingal&apos;s teach page highlights Public Speaking &amp;
        Communication as an active teaching track, and it also references
        English Language Arts in the career-growth section. Openings depend on
        current hiring needs and your fit for the role.
      </>
    ),
    acceptedAnswerTextString:
      "Codingal's teach page highlights Public Speaking & Communication as an active teaching track, and it also references English Language Arts in the career-growth section. Openings depend on current hiring needs and your fit for the role.",
  },
  {
    questionName: 'Can I teach communication skills online at Codingal?',
    acceptedAnswerText: (
      <>
        Yes. Codingal lists Public Speaking &amp; Communication as one of its
        teaching tracks for K-12 classrooms, alongside AI &amp; Coding and Math.
      </>
    ),
    acceptedAnswerTextString:
      'Yes. Codingal lists Public Speaking & Communication as one of its teaching tracks for K-12 classrooms, alongside AI & Coding and Math.',
  },
]
export const PSC_FAQ: FaqContentType[] = [
  {
    questionName:
      'Are these online public speaking classes for kids or in-person classes near me?',
    acceptedAnswerText: (
      <>
        These are live online classes, so families can join from anywhere while
        still getting real-time practice and expert teacher feedback.
      </>
    ),
    acceptedAnswerTextString:
      'These are live online classes, so families can join from anywhere while still getting real-time practice and expert teacher feedback.',
  },
  {
    questionName: 'Is this course suitable for shy kids with stage fear?',
    acceptedAnswerText: (
      <>
        Yes. Kids start with short guided speaking tasks and build confidence
        step-by-step in a supportive environment.
      </>
    ),
    acceptedAnswerTextString:
      'Yes. Kids start with short guided speaking tasks and build confidence step-by-step in a supportive environment.',
  },
  {
    questionName: 'Do you teach presentation skills for school projects?',
    acceptedAnswerText: (
      <>
        Yes. Kids learn how to start strong, organize key points, speak clearly,
        and close confidently using simple speaking structures.
      </>
    ),
    acceptedAnswerTextString:
      'Yes. Kids learn how to start strong, organize key points, speak clearly, and close confidently using simple speaking structures.',
  },
  {
    questionName:
      'Do you help with speech topics and speech ideas for kids in English?',
    acceptedAnswerText: (
      <>
        Yes. Students practice school-friendly topics using simple templates
        that make planning and delivery easier.
      </>
    ),
    acceptedAnswerTextString:
      'Yes. Students practice school-friendly topics using simple templates that make planning and delivery easier.',
  },
  {
    questionName: 'Do you cover 1 minute and 2 minute speech topics for kids?',
    acceptedAnswerText: (
      <>
        Yes. Time-based speaking drills help improve fluency, structure, and
        confidence.
      </>
    ),
    acceptedAnswerTextString:
      'Yes. Time-based speaking drills help improve fluency, structure, and confidence.',
  },
  {
    questionName: 'How do you help kids improve public speaking quickly?',
    acceptedAnswerText: (
      <>
        We focus on repeat practice with clear frameworks, frequent speaking
        turns, and feedback on voice, clarity, and structure.
      </>
    ),
    acceptedAnswerTextString:
      'We focus on repeat practice with clear frameworks, frequent speaking turns, and feedback on voice, clarity, and structure.',
  },
  {
    questionName: 'Do you support elocution or declamation style speaking?',
    acceptedAnswerText: (
      <>
        Students practice pronunciation, pauses, expression, and confident
        delivery that supports elocution and declamation formats.
      </>
    ),
    acceptedAnswerTextString:
      'Students practice pronunciation, pauses, expression, and confident delivery that supports elocution and declamation formats.',
  },
  {
    questionName: 'Do you also teach debate for kids?',
    acceptedAnswerText: (
      <>
        Debate foundations are introduced in higher grade pathways through
        structured discussions, reasoning, and rebuttal practice.
      </>
    ),
    acceptedAnswerTextString:
      'Debate foundations are introduced in higher grade pathways through structured discussions, reasoning, and rebuttal practice.',
  },
]

export const CW_FAQ: FaqContentType[] = [
  {
    questionName:
      'Are these online creative writing classes for kids or in-person classes near me?',
    acceptedAnswerText: (
      <>
        These are live online classes, so families can join from anywhere while
        still getting real-time practice and expert teacher feedback.
      </>
    ),
    acceptedAnswerTextString:
      'These are live online classes, so families can join from anywhere while still getting real-time practice and expert teacher feedback.',
  },
  {
    questionName:
      'Is this course suitable for shy kids who are reluctant to write?',
    acceptedAnswerText: (
      <>
        Yes. Kids start with short guided writing tasks and build confidence
        step-by-step in a supportive environment.
      </>
    ),
    acceptedAnswerTextString:
      'Yes. Kids start with short guided writing tasks and build confidence step-by-step in a supportive environment.',
  },
  {
    questionName: 'Do you teach writing skills for school projects?',
    acceptedAnswerText: (
      <>
        Yes. Kids learn how to start strong, organize key points, write clearly,
        and close confidently using simple story structures.
      </>
    ),
    acceptedAnswerTextString:
      'Yes. Kids learn how to start strong, organize key points, write clearly, and close confidently using simple story structures.',
  },
  {
    questionName:
      'Do you help with story topics and writing ideas for kids in English?',
    acceptedAnswerText: (
      <>
        Yes. Students practice school-friendly topics using simple templates
        that make planning and writing easier.
      </>
    ),
    acceptedAnswerTextString:
      'Yes. Students practice school-friendly topics using simple templates that make planning and writing easier.',
  },
  {
    questionName: 'Do you cover short-story and quick-write topics for kids?',
    acceptedAnswerText: (
      <>
        Yes. Time-based writing drills help improve fluency, structure, and
        confidence.
      </>
    ),
    acceptedAnswerTextString:
      'Yes. Time-based writing drills help improve fluency, structure, and confidence.',
  },
  {
    questionName: 'How do you help kids improve their writing quickly?',
    acceptedAnswerText: (
      <>
        We focus on repeat practice with clear frameworks, frequent writing
        turns, and feedback on voice, clarity, and structure.
      </>
    ),
    acceptedAnswerTextString:
      'We focus on repeat practice with clear frameworks, frequent writing turns, and feedback on voice, clarity, and structure.',
  },
  {
    questionName: 'Do you support poetry or spoken-word style writing?',
    acceptedAnswerText: (
      <>
        Students practice word choice, rhythm, expression, and confident
        storytelling that supports poetry and spoken-word formats.
      </>
    ),
    acceptedAnswerTextString:
      'Students practice word choice, rhythm, expression, and confident storytelling that supports poetry and spoken-word formats.',
  },
]

export const BENEFITS_OF_CODING_FAQ: FaqContentType[] = [
  {
    questionName: 'What age should my child start coding?',
    acceptedAnswerText: (
      <>
        Research supports starting as early as age 4-5 with screen-free or
        block-based tools.
        <Ref num={5} /> Ages 6-8 represent the highest-impact window:
        children&apos;s brains at this stage are unusually receptive to the
        structured, logical thinking that coding builds.
        <Ref num={13} /> That said, starting at any age before secondary school
        delivers real benefits. Children who begin earlier simply arrive at each
        stage with more to build on.
      </>
    ),
    acceptedAnswerTextString:
      "Research supports starting as early as age 4-5 with screen-free or block-based tools.[5] Ages 6-8 represent the highest-impact window: children's brains at this stage are unusually receptive to the structured, logical thinking that coding builds.[13] That said, starting at any age before secondary school delivers real benefits. Children who begin earlier simply arrive at each stage with more to build on.",
  },
  {
    questionName:
      'Does coding really improve problem-solving, or is that just a marketing claim?',
    acceptedAnswerText: (
      <>
        It is one of the most consistently documented findings in education
        research. A major independent review across 56 coding studies found that
        coding produces bigger improvements in problem-solving than any other
        educational activity studied.
        <Ref num={3} /> Two independent studies involving hundreds of children
        found large, measurable improvements in planning and focus.
        <Ref num={1} />
        <Ref num={2} /> The evidence is not anecdotal.
      </>
    ),
    acceptedAnswerTextString:
      'It is one of the most consistently documented findings in education research. A major independent review across 56 coding studies found that coding produces bigger improvements in problem-solving than any other educational activity studied.[3] Two independent studies involving hundreds of children found large, measurable improvements in planning and focus.[1][2] The evidence is not anecdotal.',
  },
  {
    questionName:
      'If AI can write code, is there still a reason for my child to learn it?',
    acceptedAnswerText: (
      <>
        Yes, and the reason is stronger than ever. AI generates syntax. It
        cannot define the problem worth solving, evaluate whether the output is
        correct, or reason about tradeoffs. The children who will direct AI
        tools most effectively are those who understand how code works. As Sam
        Altman observed in 2025, learning to work with AI tools is the new
        version of learning to code well.
        <Ref num={11} />
      </>
    ),
    acceptedAnswerTextString:
      'Yes, and the reason is stronger than ever. AI generates syntax. It cannot define the problem worth solving, evaluate whether the output is correct, or reason about tradeoffs. The children who will direct AI tools most effectively are those who understand how code works. As Sam Altman observed in 2025, learning to work with AI tools is the new version of learning to code well.[11]',
  },
  {
    questionName: 'Does my child need to be good at maths to learn coding?',
    acceptedAnswerText: (
      <>
        No. Research and classroom experience consistently show that children
        develop mathematical intuition through coding, rather than needing it in
        advance. Coding gives mathematical abstractions a purpose: variables,
        functions, and logic become tools the child uses to build something they
        care about. Many parents report that their child&apos;s relationship
        with mathematics improved after starting coding, not the other way
        around.
      </>
    ),
    acceptedAnswerTextString:
      "No. Research and classroom experience consistently show that children develop mathematical intuition through coding, rather than needing it in advance. Coding gives mathematical abstractions a purpose: variables, functions, and logic become tools the child uses to build something they care about. Many parents report that their child's relationship with mathematics improved after starting coding, not the other way around.",
  },
  {
    questionName:
      'How is Codingal different from a YouTube tutorial or self-paced app?',
    acceptedAnswerText: (
      <>
        Codingal delivers live, one-to-one instruction with a qualified teacher
        in every session. Research consistently shows that learning outcomes in
        coding are highest when a teacher can observe where a student&apos;s
        reasoning breaks down and guide them through it in real time.
        Pre-recorded content and passive apps cannot replicate this. Our
        curriculum is STEM.org-accredited, project-based, and designed to
        progress with each individual student.
      </>
    ),
    acceptedAnswerTextString:
      "Codingal delivers live, one-to-one instruction with a qualified teacher in every session. Research consistently shows that learning outcomes in coding are highest when a teacher can observe where a student's reasoning breaks down and guide them through it in real time. Pre-recorded content and passive apps cannot replicate this. Our curriculum is STEM.org-accredited, project-based, and designed to progress with each individual student.",
  },
  {
    questionName:
      'What subjects does coding help with beyond computer science?',
    acceptedAnswerText: (
      <>
        Documented cross-subject benefits include: mathematics (procedural
        thinking, pattern recognition, applied logic)
        <Ref num={3} />, writing and language (a Google CS First study found
        significant improvements in essay scores and writing stamina after
        coding lessons)
        <Ref num={8} />, and general metacognitive skills - the ability to plan,
        self-monitor, and self-correct.
      </>
    ),
    acceptedAnswerTextString:
      'Documented cross-subject benefits include: mathematics (procedural thinking, pattern recognition, applied logic)[3], writing and language (a Google CS First study found significant improvements in essay scores and writing stamina after coding lessons)[8], and general metacognitive skills - the ability to plan, self-monitor, and self-correct.',
  },
  {
    questionName: 'Is coding harder than mathematics?',
    acceptedAnswerText: (
      <>
        Not inherently, and for many children, coding is actually the easier
        entry point. Mathematics often requires abstract thinking in isolation;
        coding gives every abstract concept a concrete purpose. Variables become
        containers the child uses to build something. Logic becomes the rule
        that makes their game work. Many parents report that their child&apos;s
        confidence in mathematics improved after starting coding, because the
        same thinking becomes purposeful rather than theoretical. Children who
        struggle with abstract maths often find that coding gives them a way in.
      </>
    ),
    acceptedAnswerTextString:
      "Not inherently, and for many children, coding is actually the easier entry point. Mathematics often requires abstract thinking in isolation; coding gives every abstract concept a concrete purpose. Variables become containers the child uses to build something. Logic becomes the rule that makes their game work. Many parents report that their child's confidence in mathematics improved after starting coding, because the same thinking becomes purposeful rather than theoretical. Children who struggle with abstract maths often find that coding gives them a way in.",
  },
  {
    questionName:
      "Does coding increase a child's intelligence? Do coders have higher IQ?",
    acceptedAnswerText: (
      <>
        The research does not measure IQ directly, but the cognitive gains are
        well-documented and specific. A major meta-analysis across 56 coding
        studies found that coding produces the largest improvements in
        problem-solving and executive function of any educational intervention
        studied.
        <Ref num={3} /> Executive functions - which include planning, working
        memory, and inhibitory control - are the cognitive systems most strongly
        associated with academic success and real-world capability. Coding
        measurably strengthens these systems, particularly in children aged 6 to
        12.
        <Ref num={1} />
      </>
    ),
    acceptedAnswerTextString:
      'The research does not measure IQ directly, but the cognitive gains are well-documented and specific. A major meta-analysis across 56 coding studies found that coding produces the largest improvements in problem-solving and executive function of any educational intervention studied.[3] Executive functions - which include planning, working memory, and inhibitory control - are the cognitive systems most strongly associated with academic success and real-world capability. Coding measurably strengthens these systems, particularly in children aged 6 to 12.[1]',
  },
  {
    questionName:
      'What are the disadvantages or challenges of coding for kids?',
    acceptedAnswerText: (
      <>
        The honest answer: coding requires patience, and initial progress can
        feel slow. Children who expect instant results sometimes find the early
        debugging phase frustrating. Screen time is a legitimate concern for
        some parents, though structured coding instruction is categorically
        different from passive consumption - a child actively building something
        is cognitively engaged in the way that watching content is not. Cost and
        access are real barriers for many families, which is why platforms with
        free entry points matter. The research consistently shows that when
        coding instruction is well-structured, teacher-guided, and progressive,
        the challenges are temporary and the benefits are durable.
      </>
    ),
    acceptedAnswerTextString:
      'The honest answer: coding requires patience, and initial progress can feel slow. Children who expect instant results sometimes find the early debugging phase frustrating. Screen time is a legitimate concern for some parents, though structured coding instruction is categorically different from passive consumption - a child actively building something is cognitively engaged in the way that watching content is not. Cost and access are real barriers for many families, which is why platforms with free entry points matter. The research consistently shows that when coding instruction is well-structured, teacher-guided, and progressive, the challenges are temporary and the benefits are durable.',
  },
  {
    questionName: 'Is coding a good career path for children to pursue?',
    acceptedAnswerText: (
      <>
        The career case is unambiguous. The US Bureau of Labor Statistics
        projects software development roles growing at 17 percent over the next
        decade, roughly four times the average for all occupations.
        <Ref num={9} /> But the career argument extends well beyond software
        engineering. A marketing professional who can work with data, a doctor
        who can read AI diagnostics, a teacher who can build learning tools: all
        of these roles increasingly require coding and computational thinking.
        The WEF Future of Jobs Report 2025 projects 170 million new roles by
        2030, concentrated in technology, AI, and data.
        <Ref num={10} /> Children beginning coding today are not preparing for
        one career path. They are building the foundational thinking that almost
        every future role will require.
      </>
    ),
    acceptedAnswerTextString:
      'The career case is unambiguous. The US Bureau of Labor Statistics projects software development roles growing at 17 percent over the next decade, roughly four times the average for all occupations.[9] But the career argument extends well beyond software engineering. The WEF Future of Jobs Report 2025 projects 170 million new roles by 2030, concentrated in technology, AI, and data.[10] Children beginning coding today are not preparing for one career path. They are building the foundational thinking that almost every future role will require.',
  },
  {
    questionName:
      'At what age did successful people like Elon Musk start coding?',
    acceptedAnswerText: (
      <>
        Elon Musk began programming at around age 10, teaching himself BASIC
        from a manual and building a video game called Blastar, which he sold
        for approximately USD 500 at age 12. Mark Zuckerberg began coding at age
        10-11 under his father&apos;s tuition. Bill Gates started at age 13
        through his school&apos;s computer club. Steve Jobs began exploring
        electronics and programming as a teenager. The pattern across many of
        the most influential technologists of the last 50 years is consistent:
        early exposure, structured progression, and a project that mattered to
        them personally. The research on developmental windows confirms what
        their stories illustrate: starting in the primary school years produces
        outcomes that late starters cannot fully replicate.
      </>
    ),
    acceptedAnswerTextString:
      "Elon Musk began programming at around age 10, teaching himself BASIC from a manual and building a video game called Blastar, which he sold for approximately USD 500 at age 12. Mark Zuckerberg began coding at age 10-11 under his father's tuition. Bill Gates started at age 13 through his school's computer club. The pattern is consistent: early exposure, structured progression, and a project that mattered to them personally.",
  },
  {
    questionName:
      'What are the specific benefits of coding for kids in school?',
    acceptedAnswerText: (
      <>
        In a school context, coding benefits show up across multiple dimensions.
        Academic performance: students who code show measurable improvements in
        mathematics and writing, two of the core assessment subjects in most
        curricula.
        <Ref num={3} />
        <Ref num={8} /> Classroom behaviour: executive function improvements
        from coding translate into better attention, task-persistence, and
        self-regulation in class.
        <Ref num={1} /> Collaboration: coding projects taught in group or paired
        settings consistently improve communication and cooperative
        problem-solving. Teacher feedback at Codingal regularly notes that
        students who code approach classroom challenges differently: they
        decompose problems, test solutions, and treat errors as data rather than
        failures.
      </>
    ),
    acceptedAnswerTextString:
      'In a school context, coding benefits show up across multiple dimensions. Academic performance: students who code show measurable improvements in mathematics and writing.[3][8] Classroom behaviour: executive function improvements from coding translate into better attention, task-persistence, and self-regulation in class.[1] Collaboration: coding projects taught in group or paired settings consistently improve communication and cooperative problem-solving.',
  },
  {
    questionName: 'Is coding still worth learning in the age of AI?',
    acceptedAnswerText: (
      <>
        More so, not less. AI automates code generation, not code understanding.
        The engineers, analysts, and product managers who will direct AI systems
        most effectively are those who understand how code works: what it can
        and cannot do, how to evaluate its output, and how to debug it when it
        fails. Sam Altman observed in 2025 that getting good at AI tools is the
        new version of getting good at coding.
        <Ref num={11} /> For children, this means coding education is not about
        competing with AI. It is about building the foundational literacy that
        makes someone an effective director of AI, rather than a passive
        recipient of whatever it produces.
      </>
    ),
    acceptedAnswerTextString:
      'More so, not less. AI automates code generation, not code understanding. The engineers, analysts, and product managers who will direct AI systems most effectively are those who understand how code works. Sam Altman observed in 2025 that getting good at AI tools is the new version of getting good at coding.[11]',
  },
  {
    questionName:
      'What is the difference between coding and computational thinking?',
    acceptedAnswerText: (
      <>
        Coding is the act of writing instructions in a programming language.
        Computational thinking is the broader set of cognitive skills that
        coding develops: decomposing problems into smaller parts, identifying
        patterns, abstracting away unnecessary detail, and designing
        step-by-step solutions. You can have computational thinking without
        coding (a chess player or a surgeon uses it), but coding is one of the
        most effective ways to build and practise it systematically. For
        children, this distinction matters because the goal of coding education
        is not to produce programmers. It is to build a way of thinking that
        transfers across every domain they will encounter.
      </>
    ),
    acceptedAnswerTextString:
      'Coding is the act of writing instructions in a programming language. Computational thinking is the broader set of cognitive skills that coding develops: decomposing problems into smaller parts, identifying patterns, abstracting away unnecessary detail, and designing step-by-step solutions. For children, the goal of coding education is not to produce programmers. It is to build a way of thinking that transfers across every domain they will encounter.',
  },
]
